import React, { useState } from 'react';
import { ref, update } from 'firebase/database';
import { database, storage } from '../../../firebase';
import { ref as stRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../contexts/LanguageContext';
import nationalizationService, {
  addNationalizationLifecycleEventToUpdates,
  announceNationalizationWorkforceChange
} from '../../../services/nationalizationService';

// Lightweight modal to upload a signed PDF and mark agreement as signed
const SignedUploadModal = ({ ag, employeeId, companyUID, onClose }) => {
  const { t } = useTranslation(['myteam', 'common']);
  const { isRTL } = useLanguage();
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) setFile(f);
  };

  const save = async () => {
    try {
      if (!file) {
        alert(t('agreements.signedUpload.choosePdf', 'Please choose a PDF.'));
        return;
      }
      setSaving(true);
      const storagePath = `companies/${companyUID}/employees/${employeeId}/agreements/${ag.id}_${file.name}`;
      const sref = stRef(storage, storagePath);
      await uploadBytes(sref, file);
      const url = await getDownloadURL(sref);
      const base = `companies/${companyUID}/employees/${employeeId}/agreements/${ag.id}`;
      const signedAt = Date.now();
      const workforceUpdates = {
        [`${base}/url`]: url,
        [`${base}/downloadURL`]: url,
        [`${base}/storagePath`]: storagePath,
        [`${base}/status`]: 'signed',
        [`${base}/signedAt`]: signedAt
      };
      // Storage upload must finish first. Agreement metadata/status and the
      // minimized contract-review marker then commit in one RTDB transaction.
      const lifecycleEvent = addNationalizationLifecycleEventToUpdates(workforceUpdates, {
        companyUID,
        employeeId,
        source: 'employee_signed_agreement_upload',
        changeKinds: ['contract']
      });
      await update(ref(database), workforceUpdates);
      let lifecycleSync = null;
      try {
        lifecycleSync = await nationalizationService.syncEmployeeLifecycle(companyUID, employeeId, {
          eventId: lifecycleEvent.eventId,
          source: 'employee_signed_agreement_upload',
          changeKinds: ['contract']
        });
      } catch (syncError) {
        // The atomic marker remains available for mount/online/visibility replay.
        console.warn('Nationalization signed-agreement review is pending:', syncError);
      }
      announceNationalizationWorkforceChange({
        companyUID,
        source: 'employee_signed_agreement_upload',
        recalculated: false,
        syncPending: !lifecycleSync
      });
      onClose();
    } catch (err) {
      console.error('Upload signed version failed:', err);
      alert(t('agreements.signedUpload.failed', 'Failed to upload signed document.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        dir={isRTL ? 'rtl' : 'ltr'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{t('agreements.signedUpload.title', 'Upload Signed Document')}</h2>
          <div className="close" onClick={onClose}>&times;</div>
        </div>
        <div className="form-section">
          <div className="name-cell" style={{ marginBottom: 12 }}>
            <span className="cell-text">{ag?.name || t('agreements.signedUpload.agreement', 'Agreement')}</span>
          </div>
          <div className="input-group">
            <input type="file" accept="application/pdf" onChange={onFile} />
            {file && <span className="micro-subtext" style={{ marginTop: 6 }}>{file.name}</span>}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>{t('common:buttons.cancel', 'Cancel')}</button>
          <button className="btn-primary" onClick={save} disabled={saving}>
            {saving
              ? t('agreements.signedUpload.uploading', 'Uploading…')
              : t('common:buttons.save', 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignedUploadModal;



